import { useState, useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';

import type { Provider, CollabEditProvider } from '@atlaskit/collab-provider';
import { expValEquals } from '@confluence/feature-experiments';
import { isDefaultLiveDocTitle } from '@confluence/live-pages-utils/entry-points/isDefaultLiveDocTitle.ts';

import { useNativeCollabState } from './useNativeCollabState.ts';

export const useMetadataChanged = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	onMetadataChange?: () => void,
	isLivePage?: boolean,
	isRovoLLMEnabled?: boolean,
	isDocEmptyRef?: MutableRefObject<boolean>,
) => {
	const [title, setTitle] = useState<string | null>(null);
	const [{ collabEditInitialized }] = useNativeCollabState();
	// Use a ref so the event handler always reads the latest collabEditInitialized
	// value without needing to re-register the handler on every state change.
	const collabEditInitializedRef = useRef(collabEditInitialized);
	collabEditInitializedRef.current = collabEditInitialized;

	const onTitleChange = useCallback(
		(newTitle: string) => {
			if (collabEditProvider) {
				setTitle(newTitle);
				const metadata = collabEditProvider.getMetadata();
				collabEditProvider.setMetadata({ ...metadata, metadataSource: 'client', title: newTitle });
			}
		},
		[collabEditProvider],
	);
	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}
		const handleTitleChanged = (data) => {
			if (!data || !('title' in data)) {
				return;
			}
			const { title } = data;
			// For cwr_blank_object_experiment variant1 and variant2 on live docs,
			// only suppress the auto-generated title on initial load (before collab
			// has initialized). Once initialized, metadata:changed events represent
			// user edits and should pass through normally.
			// Only suppress when AI is enabled, the page is a live page, the doc
			// was empty at init time (BOE experience is shown), not yet initialized,
			// and the experiment is active.
			const shouldSuppressTitle =
				isLivePage &&
				isRovoLLMEnabled &&
				isDocEmptyRef?.current &&
				!collabEditInitializedRef.current &&
				isDefaultLiveDocTitle(title) &&
				(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2'));
			setTitle(shouldSuppressTitle ? '' : title);
			onMetadataChange?.();
		};
		collabEditProvider.on('metadata:changed', handleTitleChanged);
		return () => {
			collabEditProvider.off('metadata:changed', handleTitleChanged);
		};
	}, [collabEditProvider, onMetadataChange, isLivePage, isRovoLLMEnabled, isDocEmptyRef]);

	return {
		onTitleChange,
		title,
	};
};
